/**
 * The theme's single accordion behaviour.
 *
 * WHAT THE THREE IMPLEMENTATIONS ACTUALLY DID (D-005's open question, measured)
 * ---------------------------------------------------------------------------
 *                    close others within   opens by          aria-expanded  keyboard
 *   faq.liquid       .faq__list (scoped)   inline maxHeight  set on both    Enter/Space
 *   faq-page.liquid  the WHOLE DOCUMENT    inline maxHeight  never set      none
 *   delivery-page    the WHOLE DOCUMENT    .is-open class    set on both    none
 *
 * Two of those columns are defects rather than design:
 *
 *  1. `faq-page.liquid` binds `document.querySelectorAll('.faqp-item__q')` —
 *     UNSCOPED, on a class it SHARES with `faq.liquid`, which scopes itself to
 *     `.faq .faqp-item__q`. They sit on different templates today so nothing
 *     misfires. Put both on one page and every homepage FAQ item gets two
 *     handlers: the first opens it, the second immediately closes it, and the
 *     accordion appears dead. That is a trap for whoever next adds the FAQ
 *     section to a page.
 *  2. The FAQ page's questions carry no `role`, no `tabindex` and no
 *     `aria-expanded` — they are plain `<div>`s. The whole page is unreachable
 *     by keyboard and silent to a screen reader. The homepage FAQ, with the
 *     same visual design, gets all three.
 *
 * WHAT THIS CHANGES
 * -----------------
 * Nothing visual, and no markup: each site keeps its own scope, its own open
 * mechanism and its own classes, passed in as configuration. D1 governs those —
 * `delivery-page`'s CSS transition caps at 400px and `faq`'s measures
 * scrollHeight, and swapping one for the other would change how long tall
 * answers take to open.
 *
 * What is unified is the keyboard and ARIA contract, which moves no pixels: every
 * accordion trigger becomes focusable, operable with Enter and Space, and
 * reports its state.
 */
(function () {
  /**
   * @param {string} triggerSelector
   * @param {object} cfg
   * @param {(t: Element) => Element} cfg.scope    where "close the others" applies
   * @param {(t: Element) => Element} [cfg.item]   element carrying the open class
   * @param {(t: Element) => Element} cfg.panel    the answer element
   * @param {string} [cfg.itemOpenClass]           class toggled on the item
   * @param {string} [cfg.panelOpenClass]          class toggled on the panel
   * @param {boolean} [cfg.measure]                set max-height from scrollHeight
   */
  function bind(triggerSelector, cfg) {
    const triggers = Array.from(document.querySelectorAll(triggerSelector));
    if (!triggers.length) return;

    const isOpen = (t) => {
      if (cfg.itemOpenClass) {
        const item = cfg.item(t);
        return Boolean(item && item.classList.contains(cfg.itemOpenClass));
      }
      return t.getAttribute('aria-expanded') === 'true';
    };

    const close = (t) => {
      t.setAttribute('aria-expanded', 'false');
      const panel = cfg.panel(t);
      if (cfg.itemOpenClass) {
        const item = cfg.item(t);
        if (item) item.classList.remove(cfg.itemOpenClass);
      }
      if (!panel) return;
      if (cfg.panelOpenClass) panel.classList.remove(cfg.panelOpenClass);
      if (cfg.measure) panel.style.maxHeight = '';
      panel.setAttribute('aria-hidden', 'true');
    };

    const open = (t) => {
      t.setAttribute('aria-expanded', 'true');
      const panel = cfg.panel(t);
      if (cfg.itemOpenClass) {
        const item = cfg.item(t);
        if (item) item.classList.add(cfg.itemOpenClass);
      }
      if (!panel) return;
      if (cfg.panelOpenClass) panel.classList.add(cfg.panelOpenClass);
      // scrollHeight must be read AFTER the class is applied: the panel is
      // `max-height: 0` while closed, and measuring first reads 0 on a browser
      // that has not yet reflowed.
      if (cfg.measure) panel.style.maxHeight = `${panel.scrollHeight}px`;
      panel.setAttribute('aria-hidden', 'false');
    };

    for (const trigger of triggers) {
      // The ARIA and keyboard contract, applied uniformly. The FAQ page's
      // triggers are bare <div>s in the ported markup; adding these attributes
      // changes no box and no pixel, and makes the page operable.
      if (!trigger.hasAttribute('role') && trigger.tagName !== 'BUTTON') {
        trigger.setAttribute('role', 'button');
      }
      if (!trigger.hasAttribute('tabindex') && trigger.tagName !== 'BUTTON') {
        trigger.setAttribute('tabindex', '0');
      }
      if (!trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', String(isOpen(trigger)));
      }

      const toggle = () => {
        const wasOpen = isOpen(trigger);
        const scope = cfg.scope(trigger) || document;
        // Close everything in scope first — including this one, so the
        // "already open" case falls out as a plain close.
        scope.querySelectorAll(triggerSelector).forEach(close);
        if (!wasOpen) open(trigger);
      };

      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        toggle();
      });
    }
  }

  window.GlowmingAccordion = { bind };
})();
